import { useState } from 'react'
import type { UserDetail } from '../../services/adminApi'
import { updateUser } from '../../services/adminApi'
import { CustomButton } from '../../components/common/CustomButton'
import { CustomTextInput } from '../../components/common/CustomTextInput'
import { usePermission } from '../../hooks/usePermission'
import { cdnImage } from '../../utils/cdnImage'

interface UserDetailDrawerProps {
  user: UserDetail
  onClose: () => void
  onSuspend: (reason: string, notes?: string) => Promise<void>
  onUnsuspend: (notes?: string) => Promise<void>
  onBan: (reason: string) => Promise<void>
  onExport: () => Promise<void>
  onUserUpdated?: (user: UserDetail) => void
}

export function UserDetailDrawer({
  user,
  onClose,
  onSuspend,
  onUnsuspend,
  onBan,
  onExport,
  onUserUpdated,
}: UserDetailDrawerProps) {
  const [action, setAction] = useState<'suspend' | 'ban' | null>(null)
  const [reason, setReason] = useState('')
  const [notes, setNotes] = useState('')
  const [loading, setLoading] = useState(false)

  const [editingField, setEditingField] = useState<'email' | 'phone' | null>(null)
  const [editValue, setEditValue] = useState('')
  const [editLoading, setEditLoading] = useState(false)
  const [editError, setEditError] = useState<string | null>(null)

  const canEdit = usePermission('users', 'edit')
  const canDelete = usePermission('users', 'delete')

  const startEdit = (field: 'email' | 'phone') => {
    setEditingField(field)
    setEditValue(field === 'email' ? (user.email || '') : (user.phone || ''))
    setEditError(null)
  }

  const handleSaveField = async () => {
    if (!editingField) return
    setEditLoading(true)
    setEditError(null)
    const result = await updateUser(user.id, { [editingField]: editValue })
    setEditLoading(false)
    if (result.ok && result.data) {
      setEditingField(null)
      onUserUpdated?.(result.data)
    } else {
      setEditError(result.error?.message || 'Failed to update')
    }
  }

  const handleAction = async () => {
    setLoading(true)
    try {
      if (action === 'suspend') {
        await onSuspend(reason, notes || undefined)
      } else if (action === 'ban') {
        await onBan(reason)
      }
      setAction(null)
      setReason('')
      setNotes('')
    } catch {
      // Error handling done in parent
    } finally {
      setLoading(false)
    }
  }

  const handleUnsuspend = async () => {
    setLoading(true)
    try {
      await onUnsuspend()
    } finally {
      setLoading(false)
    }
  }

  const handleExport = async () => {
    setLoading(true)
    try {
      await onExport()
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div
        className="h-full w-full max-w-md overflow-y-auto bg-white dark:bg-[#1a1520] p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-6">
          <h2 className="font-heading text-xl font-bold text-gray-900 dark:text-gray-100">User Details</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 cursor-pointer">
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* User Info */}
        <div className="mb-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
              {user.avatar_url ? (
                <img src={cdnImage(user.avatar_url, { w: 128, q: 80 }) ?? user.avatar_url} alt={user.name || ''} className="h-full w-full rounded-full object-cover" />
              ) : (
                <span className="text-2xl font-bold text-primary">
                  {(user.name || user.email).charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{user.name || 'No name'}</h3>
              <p className="text-gray-500 dark:text-gray-400">{user.email}</p>
            </div>
          </div>

          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Status</span>
              <span className="font-medium capitalize text-gray-900 dark:text-gray-100">{user.status}</span>
            </div>
            {editingField === 'email' ? (
              <div>
                <CustomTextInput
                  label="Email"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="user@example.com"
                />
                {editError && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{editError}</p>}
                <div className="flex gap-2 mt-2">
                  <CustomButton size="sm" onClick={handleSaveField} loading={editLoading} variant="primary">Save</CustomButton>
                  <CustomButton size="sm" onClick={() => setEditingField(null)} variant="secondary">Cancel</CustomButton>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Email</span>
                <span className="flex items-center gap-2">
                  <span className="text-gray-900 dark:text-gray-100">{user.email || 'Not provided'}</span>
                  {canEdit && (
                    <button onClick={() => startEdit('email')} className="text-primary dark:text-[#b58fcc] text-xs hover:underline cursor-pointer">edit</button>
                  )}
                </span>
              </div>
            )}
            {editingField === 'phone' ? (
              <div>
                <CustomTextInput
                  label="Phone"
                  value={editValue}
                  onChange={(e) => setEditValue(e.target.value)}
                  placeholder="+1234567890"
                />
                {editError && <p className="text-xs text-red-600 dark:text-red-400 mt-1">{editError}</p>}
                <div className="flex gap-2 mt-2">
                  <CustomButton size="sm" onClick={handleSaveField} loading={editLoading} variant="primary">Save</CustomButton>
                  <CustomButton size="sm" onClick={() => setEditingField(null)} variant="secondary">Cancel</CustomButton>
                </div>
              </div>
            ) : (
              <div className="flex justify-between items-center">
                <span className="text-gray-500 dark:text-gray-400">Phone</span>
                <span className="flex items-center gap-2">
                  <span className="text-gray-900 dark:text-gray-100">{user.phone || 'Not provided'}</span>
                  {canEdit && (
                    <button onClick={() => startEdit('phone')} className="text-primary dark:text-[#b58fcc] text-xs hover:underline cursor-pointer">edit</button>
                  )}
                </span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Email Verified</span>
              <span className="text-gray-900 dark:text-gray-100">{user.email_verified ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Phone Verified</span>
              <span className="text-gray-900 dark:text-gray-100">{user.phone_verified ? 'Yes' : 'No'}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Joined</span>
              <span className="text-gray-900 dark:text-gray-100">{new Date(user.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-500 dark:text-gray-400">Last Active</span>
              <span className="text-gray-900 dark:text-gray-100">{user.last_active_at ? new Date(user.last_active_at).toLocaleDateString() : 'N/A'}</span>
            </div>
            {user.shop_name && (
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Shop</span>
                <span className="text-primary dark:text-[#b58fcc]">{user.shop_name}</span>
              </div>
            )}
            {user.admin_role && (
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Admin Role</span>
                <span className="font-medium capitalize text-gray-900 dark:text-gray-100">{user.admin_role}</span>
              </div>
            )}
          </div>
        </div>

        {/* Moderation History */}
        {user.moderation_notes && (
          <div className="mb-6 rounded-xl bg-gray-50 dark:bg-gray-800 p-4">
            <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Moderation Notes</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{user.moderation_notes}</p>
            {user.moderated_at && (
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                Moderated on {new Date(user.moderated_at).toLocaleString()}
              </p>
            )}
          </div>
        )}

        {/* Action Form */}
        {action && (
          <div className="mb-6 rounded-xl border border-gray-200 dark:border-gray-700 p-4">
            <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-3 capitalize">{action} User</h4>
            <CustomTextInput
              label="Reason (required)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Enter reason..."
            />
            {action === 'suspend' && (
              <CustomTextInput
                label="Notes (optional)"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Additional notes..."
              />
            )}
            <div className="mt-4 flex gap-2">
              <CustomButton
                onClick={handleAction}
                disabled={loading || !reason}
                variant="danger"
              >
                {loading ? 'Processing...' : `Confirm ${action}`}
              </CustomButton>
              <CustomButton onClick={() => setAction(null)} variant="secondary">
                Cancel
              </CustomButton>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        {!action && (
          <div className="space-y-3">
            {user.status === 'active' && canEdit && (
              <CustomButton onClick={() => setAction('suspend')} variant="ghost" className="w-full justify-center border border-amber-300 dark:border-amber-600 text-amber-700 dark:text-amber-400 hover:bg-amber-50 dark:hover:bg-amber-900/20">
                Suspend User
              </CustomButton>
            )}
            {user.status === 'suspended' && canEdit && (
              <CustomButton onClick={handleUnsuspend} disabled={loading} variant="success" className="w-full justify-center">
                Unsuspend User
              </CustomButton>
            )}
            {user.status !== 'banned' && user.status !== 'deleted' && canDelete && (
              <CustomButton onClick={() => setAction('ban')} variant="ghost" className="w-full justify-center border border-red-300 dark:border-red-700 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20">
                Ban User
              </CustomButton>
            )}
            <CustomButton onClick={handleExport} disabled={loading} variant="secondary" className="w-full justify-center">
              Export User Data
            </CustomButton>
          </div>
        )}
      </div>
    </div>
  )
}
